import React from "react";
import { Search, ArrowUpDown, Trash2, Eraser } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useApp } from "@/context/AppContext";

const FILTERS = [
  { key: "all", labelKey: "filterAll" },
  { key: "active", labelKey: "filterActive" },
  { key: "done", labelKey: "filterDone" },
];

const ConfirmButton = ({ testId, icon: Icon, label, title, desc, onConfirm, disabled }) => {
  const { t } = useApp();
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          data-testid={testId}
          disabled={disabled}
          className="flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-40 disabled:pointer-events-none"
        >
          <Icon className="w-4 h-4" />
          <span className="hidden md:inline">{label}</span>
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{desc}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel">{t("cancel")}</AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-delete"
            onClick={onConfirm}
            className="bg-destructive text-destructive-foreground hover:brightness-110"
          >
            {t("delete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const Toolbar = ({
  filter,
  setFilter,
  search,
  setSearch,
  sort,
  setSort,
  onDeleteCompleted,
  onDeleteAll,
  hasCompleted,
  hasTasks,
}) => {
  const { t } = useApp();

  return (
    <div className="space-y-3">
      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2" />
        <input
          data-testid="search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("search")}
          className="w-full h-11 bg-card border border-border rounded-xl pl-11 pr-4 text-sm outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Filter tabs */}
        <div className="inline-flex p-1 rounded-xl bg-secondary" data-testid="filter-tabs">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}-btn`}
              onClick={() => setFilter(f.key)}
              className={`px-4 h-9 rounded-lg text-sm font-medium transition-all ${
                filter === f.key
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t(f.labelKey)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {/* Sort */}
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="sort-select" className="h-9 w-auto gap-2 rounded-lg bg-card text-sm">
              <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              <SelectItem value="newest">{t("sortNewest")}</SelectItem>
              <SelectItem value="oldest">{t("sortOldest")}</SelectItem>
              <SelectItem value="alpha">{t("sortAlpha")}</SelectItem>
              <SelectItem value="priority">{t("sortPriority")}</SelectItem>
              <SelectItem value="doneLast">{t("sortDoneLast")}</SelectItem>
            </SelectContent>
          </Select>

          <ConfirmButton
            testId="delete-completed-btn"
            icon={Eraser}
            label={t("deleteDone")}
            title={t("confirmDoneTitle")}
            desc={t("confirmDoneDesc")}
            onConfirm={onDeleteCompleted}
            disabled={!hasCompleted}
          />
          <ConfirmButton
            testId="delete-all-btn"
            icon={Trash2}
            label={t("deleteAll")}
            title={t("confirmAllTitle")}
            desc={t("confirmAllDesc")}
            onConfirm={onDeleteAll}
            disabled={!hasTasks}
          />
        </div>
      </div>
    </div>
  );
};
